import tippy from "tippy.js";
import type { Instance } from "tippy.js";
import type { Editor } from "@tiptap/core";
import type {
  SuggestionOptions,
  SuggestionProps,
  SuggestionKeyDownProps,
} from "@tiptap/suggestion";
import type { GetReferenceClientRect } from "tippy.js";
import ToolList from "./ToolList.svelte";
import { TOOL_OPTIONS } from "./toolConstants";

interface CommandProps {
  editor: Editor;
  range: { from: number; to: number };
  props: { result: string };
}

interface ToolSuggestionProps extends SuggestionProps {
  event?: KeyboardEvent;
  editor: Editor;
}

export const toolSuggestion = (): Partial<SuggestionOptions> => ({
  items: ({ query }: { query: string }) => [
    TOOL_OPTIONS.filter((tool: string) => tool.toLowerCase().includes(query.toLowerCase())).slice(
      0,
      10
    ),
  ],
  command: ({ editor, range, props }: CommandProps) => {
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent({
        type: "toolInlineBlock",
        attrs: { tool: props.result },
      })
      .run();
  },
  render: () => {
    let component: ToolList;
    let popup: Instance | undefined;

    return {
      onStart: (props: ToolSuggestionProps) => {
        if (!props.clientRect || !props.editor) {
          return;
        }

        const el = document.createElement("div");

        // Add a click handler to capture clicks within the dropdown to prevent form submission
        el.addEventListener("click", (e) => {
          e.preventDefault();
        });

        component = new ToolList({
          props: {
            items: props.items?.[0] || [],
            command: async (params) => {
              try {
                // Execute the command with the tool name
                await props.command?.({
                  result: params.result,
                });

                // Clean up popup after successful insertion
                if (popup) {
                  popup.hide();
                  setTimeout(() => {
                    popup?.destroy();
                  }, 100);
                }
              } catch (error) {
                console.error("Error executing tool command:", error);
              }
            },
          },
          target: el,
        });

        const getReferenceClientRect: GetReferenceClientRect = () => {
          const rect =
            typeof props.clientRect === "function" ? props.clientRect() : props.clientRect;
          return rect || new DOMRect();
        };

        popup = tippy(document.body, {
          getReferenceClientRect,
          appendTo: () => document.body,
          content: el,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },

      onUpdate(props: ToolSuggestionProps) {
        component?.$set({
          items: props.items?.[0] || [],
        });

        if (!props.clientRect) {
          return;
        }

        const getReferenceClientRect: GetReferenceClientRect = () => {
          const rect =
            typeof props.clientRect === "function" ? props.clientRect() : props.clientRect;
          return rect || new DOMRect();
        };

        popup?.setProps({
          getReferenceClientRect,
        });
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event) {
          if (props.event.key === "Escape") {
            popup?.hide();
            props.event.preventDefault();
            return true;
          }

          if (["ArrowUp", "ArrowDown", "Enter"].includes(props.event.key)) {
            // Prevent default behavior for these keys in forms
            props.event.preventDefault();

            // ignore keypress as we are already handling these in ToolList.svelte
            return true;
          }
        }
        return false;
      },

      onExit() {
        popup?.destroy();
        component?.$destroy();
      },
    };
  },
});
